<template>
  <div class="page-history">
    <div v-if="loading">Loading...</div>
    <div v-else-if="!pageHistoryEvents.length">There is no history available for this page.</div>
    <div v-else-if="pageHistoryEvents.length">
      <HxButton
        id="getCurrentContentBtn"
        label="Return to Current Version"
        variant="primary"
        @click="getCurrentContentVersion()"
      />

      <v-timeline density="compact" size="x-small" class="page-history-timeline">
        <v-slide-x-transition group>
          <v-timeline-item
            v-for="(event, index) in pageHistoryEvents"
            :key="event.id"
            class="mb-4"
            dot-color="primary"
          >
            <v-row
              :class="[
                'd-flex',
                'flex-row',
                'justify-space-between',
                'page-history-event',
                index !== 0 ? 'clickable' : 'non-clickable',
                event.id == pageCommitOID() ? 'selected' : ''
              ]"
              @click="index !== 0 && getPreviousContentVersion(event)"
            >
              <v-col cols="12" align="left">
                <div class="page-history-date">{{ convertDateFormat(event.date) }}</div>
                <span
                  class="text-break page-history-summary text-left"
                  :title="event.publishSummary || 'Module Published'"
                >
                  {{ event.publishSummary || 'Module Published' }}
                </span>
              </v-col>
            </v-row>
          </v-timeline-item>
        </v-slide-x-transition>
      </v-timeline>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import { CSBase } from '@complispace/cs-design-system';
import * as types from '@/store/mutationTypes';
import EventBus from '@/bus';
import { EVENTBUS_CMD } from '@/constants';

export default {
  name: 'PageHistory',

  extends: CSBase,

  mounted() {
    this.getPageHistory(); // async
  },

  data() {
    return {
      loading: false
    };
  },

  computed: {
    ...mapState({
      pageHistoryEvents: (state) => state.data.pageHistory
    }),

    ...mapGetters({
      pageCommitOID: 'data/getPageCommitOID'
    }),

    moduleId() {
      const { params } = this.$route;
      const rawModuleId = params?.moduleId;
      const moduleId = parseInt(rawModuleId, 10);
      return moduleId;
    },

    pagePath() {
      const { params } = this.$route;
      const pagePath = params?.pagePath;
      return pagePath;
    }
  },

  watch: {
    loading(newValue) {
      this.setLoading(newValue);
    },
    $route() {
      this.getPageHistory();
    }
  },

  methods: {
    ...mapMutations({
      setPageCommitOID: types.DATA_SET_PAGE_COMMIT_OID,
      setPageCommitTimestamp: types.DATA_SET_PAGE_COMMIT_TIMESTAMP
    }),

    async getPageHistory() {
      if (this.loading) return;
      try {
        this.loading = true;
        const details = {
          moduleId: this.moduleId,
          pagePath: this.pagePath
        };
        if (!details.moduleId || !details.pagePath) {
          return;
        }
        this.$store.commit(types.DATA_CLEAR_PAGE_HISTORY); // Clear the page previous page history before we fetch new page history
        await this.$store.dispatch('data/getPageHistory', details);
      } catch (err) {
        this.$store.commit(types.DATA_CLEAR_PAGE_HISTORY);
      } finally {
        this.loading = false;
      }
    },

    getPreviousContentVersion(event) {
      const { id, date } = event;

      this.setPageCommitOID(id);
      this.setPageCommitTimestamp(date);
    },

    getCurrentContentVersion() {
      EventBus.$emit(EVENTBUS_CMD.HARD_REFRESH);
      this.setPageCommitOID('');
      this.setPageCommitTimestamp('');
    },

    convertDateFormat(date) {
      return moment
        .tz(date, 'Australia/Sydney')
        .format('DD/MM/YYYY HH:mm:ss')
        .replace(/:[^:]*$/, '');
    }
  }
};
</script>

<style scoped>
#getCurrentContentBtn {
  width: 100%;
}
.page-history {
  display: flex;
  flex-direction: column;

  text-align: center;
  white-space: normal;
}
.page-history-timeline {
  padding-top: 12px;
  padding-bottom: 12px;
}

.page-history-event.clickable:hover {
  cursor: pointer;
  text-decoration: underline;
}

.page-history-event.clickable:hover .page-history-summary {
  cursor: pointer;
  text-decoration: underline;
}

.page-history-event.non-clickable {
  & .page-history-date,
  & .page-history-summary {
    color: rgba(0, 0, 0, 0.38);
  }
}
.page-history-event.selected {
  & .page-history-date,
  & .page-history-summary {
    color: rgb(var(--v-theme-primary));
  }
}

.page-history-date {
  color: #2f4051;
  /* Style-07 */
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0px;
}

.page-history-summary {
  color: #2f4051;
  /* Style-11 */
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;

  text-align: justify;
  overflow: hidden;

  /* line-clamp to 2 lines */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  line-height: 1.5;
  max-height: 3em; /* 2 lines × 1.5 line-height = 3em */
}
</style>
